import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Card,
    Chip,
    FormControl,
    IconButton,
    MenuItem,
    Pagination,
    Select,
    Stack,
    Typography
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import Swal from 'sweetalert2';
import { liftTruckPmService } from '../../../services/liftTruckPmService';
import { POWER_TYPE_LABELS, getItemKeys, isItemDone } from './liftTruckPmSchema';

const LiftTruckPmList = () => {
    const navigate = useNavigate();
    const [forms, setForms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    useEffect(() => {
        loadForms();
    }, []);

    const loadForms = async () => {
        setLoading(true);
        try {
            const data = await liftTruckPmService.getAllFromIndexedDB();
            // Отправленные формы здесь не показываем — они уже на сервере.
            const sorted = (data || [])
                .filter((f) => f.status !== 'submitted')
                .sort((a, b) => new Date(b.updatedAt || b.createdAt || 0) - new Date(a.updatedAt || a.createdAt || 0));
            setForms(sorted);
        } catch (error) {
            console.error('Error loading lift truck PM inspections:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (uuid) => {
        const result = await Swal.fire({
            title: 'Delete this form?',
            text: 'This draft will be removed from this device.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it',
        });
        if (!result.isConfirmed) return;

        try {
            await liftTruckPmService.deleteFromIndexedDB(uuid);
            loadForms();
        } catch (error) {
            console.error('Error deleting lift truck PM inspection:', error);
            Swal.fire({ icon: 'error', title: 'Error', text: 'Could not delete the draft' });
        }
    };

    const paginatedForms = useMemo(() => {
        const start = (currentPage - 1) * itemsPerPage;
        return forms.slice(start, start + itemsPerPage);
    }, [forms, currentPage, itemsPerPage]);

    const totalPages = Math.ceil(forms.length / itemsPerPage);

    const cardTitle = (rec) => {
        const code = rec.equipmentCode || '';
        const description = rec.equipmentDescription || '';
        if (code || description) return `${code}${code && description ? ' — ' : ''}${description}`;
        return 'No equipment selected';
    };

    const progress = (rec) => {
        const keys = getItemKeys(rec.powerType);
        const checklist = rec.checklist || {};
        const comments = rec.itemComments || {};
        return {
            checked: keys.filter((key) => isItemDone(checklist[key], comments[key])).length,
            total: keys.length,
            comments: keys.filter((key) => String(comments[key] || '').trim() !== '').length,
        };
    };

    return (
        <Box sx={{ height: 'calc(100vh - 60px)', backgroundColor: '#f5f5f5', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Box sx={{ backgroundColor: 'white', borderBottom: '1px solid #e0e0e0', p: 1.5, flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton onClick={() => navigate('/main')} edge="start">
                            <ArrowBackIcon />
                        </IconButton>
                        <Typography variant="h6" fontWeight={600}>Lift Truck PM Inspection</Typography>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => navigate('/operations/lift-truck-pm/form')}
                        sx={{ backgroundColor: '#4caf50', '&:hover': { backgroundColor: '#45a049' } }}
                    >
                        New Form
                    </Button>
                </Box>
            </Box>

            <Box sx={{ p: 1.5, flexGrow: 1, overflowY: 'auto' }}>
                {loading ? (
                    <Card sx={{ p: 4 }}><Typography>Loading...</Typography></Card>
                ) : forms.length === 0 ? (
                    <Card sx={{ p: 4, textAlign: 'center' }}>
                        <Typography variant="h6" color="text.secondary">No forms found</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Click "New Form" to create one
                        </Typography>
                    </Card>
                ) : (
                    <>
                        <Stack spacing={1}>
                            {paginatedForms.map((rec) => {
                                const { checked, total, comments } = progress(rec);

                                return (
                                    <Card key={rec.uuid} sx={{ p: 1.5 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Box sx={{ flex: 1 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                    <Typography variant="subtitle1" sx={{ fontSize: '0.95rem' }}>
                                                        {cardTitle(rec)}
                                                    </Typography>
                                                    <Chip label="Draft" color="warning" size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                                                    {rec.powerType && (
                                                        <Chip
                                                            label={POWER_TYPE_LABELS[rec.powerType]}
                                                            size="small"
                                                            variant="outlined"
                                                            sx={{ height: 20, fontSize: '0.7rem' }}
                                                        />
                                                    )}
                                                    {total > 0 && (
                                                        <Chip
                                                            label={`${checked}/${total} checked`}
                                                            color={checked === total ? 'success' : 'default'}
                                                            size="small"
                                                            sx={{ height: 20, fontSize: '0.7rem' }}
                                                        />
                                                    )}
                                                    {comments > 0 && (
                                                        <Chip label={`${comments} comment${comments === 1 ? '' : 's'}`} color="primary" size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                                                    )}
                                                </Box>
                                                <Stack direction="row" spacing={2} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Date: {rec.inspectionDate ? dayjs(rec.inspectionDate).format('MM/DD/YYYY') : '—'}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Hour meter: {rec.hourMeter || '—'}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Updated: {dayjs(rec.updatedAt || rec.createdAt).format('MM/DD/YYYY HH:mm')}
                                                    </Typography>
                                                </Stack>
                                            </Box>
                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={() => navigate(`/operations/lift-truck-pm/form?uuid=${encodeURIComponent(rec.uuid)}`)}
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton size="small" color="error" onClick={() => handleDelete(rec.uuid)}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        </Box>
                                    </Card>
                                );
                            })}
                        </Stack>

                        {totalPages > 1 && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                                <FormControl size="small" sx={{ minWidth: 120 }}>
                                    <Select
                                        value={itemsPerPage}
                                        onChange={(e) => { setItemsPerPage(e.target.value); setCurrentPage(1); }}
                                    >
                                        <MenuItem value={5}>5 per page</MenuItem>
                                        <MenuItem value={10}>10 per page</MenuItem>
                                        <MenuItem value={25}>25 per page</MenuItem>
                                        <MenuItem value={50}>50 per page</MenuItem>
                                    </Select>
                                </FormControl>
                                <Pagination
                                    count={totalPages}
                                    page={currentPage}
                                    onChange={(e, value) => setCurrentPage(value)}
                                    color="primary"
                                    showFirstButton
                                    showLastButton
                                />
                            </Box>
                        )}
                    </>
                )}
            </Box>
        </Box>
    );
};

export default LiftTruckPmList;
